import { supabase } from "../supabaseClient"

export const diasDaSemana = ["seg", "ter", "qua", "qui", "sex"]

export const turnos = {
  manha: [
    { inicio: "07:30", fim: "08:15" },
    { inicio: "08:15", fim: "09:00" },
    { inicio: "09:05", fim: "09:50" },
    { inicio: "09:50", fim: "10:35" },
    { inicio: "10:50", fim: "11:35" },
    { inicio: "11:35", fim: "12:20" },
  ],
  tarde: [
    { inicio: "13:00", fim: "13:45" },
    { inicio: "13:45", fim: "14:30" },
    { inicio: "14:35", fim: "15:20" },
    { inicio: "15:20", fim: "16:05" },
    { inicio: "16:10", fim: "16:55" },
    { inicio: "16:55", fim: "17:40" },
  ],
  noite: [
    { inicio: "18:00", fim: "18:45" },
    { inicio: "18:45", fim: "19:30" },
    { inicio: "19:35", fim: "20:20" },
    { inicio: "20:20", fim: "21:05" },
    { inicio: "21:10", fim: "21:55" },
    { inicio: "21:55", fim: "22:40" },
  ],
}

const turnosAlias = {
  manhã: "manha",
  manha: "manha",
  tarde: "tarde",
  noite: "noite",
}

function shuffleArray(array) {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

function distribuirDisciplinasAleatorio(disciplinasComTempos, diasDisponiveis, maxTemposPorDia = 4) {
  const distribuicao = []

  const diasEmbaralhados = shuffleArray(diasDisponiveis)

  const slotsDisponiveis = {}
  diasEmbaralhados.forEach((dia) => {
    slotsDisponiveis[dia] = [0, 1, 2, 3, 4, 5]
  })

  const nucleares = shuffleArray(disciplinasComTempos.filter((d) => d.tipo === "N"))
  const globais = shuffleArray(disciplinasComTempos.filter((d) => d.tipo !== "N"))
  const disciplinasOrdenadas = [...nucleares, ...globais]

  disciplinasOrdenadas.forEach((disc) => {
    let temposRestantes = disc.tempos_semanais || 4

    const diasParaDistribuir = shuffleArray(Object.keys(slotsDisponiveis).filter((d) => slotsDisponiveis[d].length > 0))
    const contadorPorDia = {}
    diasParaDistribuir.forEach((dia) => (contadorPorDia[dia] = 0))

    while (temposRestantes > 0) {
      let alocado = false
      const chunkSizes = []

      if (maxTemposPorDia >= 4 && disc.tipo === "N" && temposRestantes >= 4) {
        chunkSizes.push(4)
      }

      for (let chunk = Math.min(maxTemposPorDia, temposRestantes); chunk >= 1; chunk--) {
        if (!chunkSizes.includes(chunk)) {
          chunkSizes.push(chunk)
        }
      }

      for (const chunk of chunkSizes) {
        if (alocado) break

        for (const dia of diasParaDistribuir) {
          if (contadorPorDia[dia] + chunk > maxTemposPorDia) continue

          const slots = slotsDisponiveis[dia]
          if (slots.length < chunk) continue

          if (chunk === 1) {
            const slot = slots[0]
            distribuicao.push({ disciplina: disc, dia: dia, tempoIndex: slot })
            slotsDisponiveis[dia] = slots.filter((s) => s !== slot)
            temposRestantes -= 1
            contadorPorDia[dia] += 1
            alocado = true
            break
          }

          for (let i = 0; i <= slots.length - chunk; i++) {
            let consecutivos = true
            for (let j = 1; j < chunk; j++) {
              if (slots[i + j] !== slots[i] + j) {
                consecutivos = false
                break
              }
            }
            if (!consecutivos) continue

            for (let j = 0; j < chunk; j++) {
              distribuicao.push({ disciplina: disc, dia: dia, tempoIndex: slots[i + j] })
            }
            const inicio = slots[i]
            const fim = slots[i + chunk - 1]
            slotsDisponiveis[dia] = slots.filter((s) => s < inicio || s > fim)
            temposRestantes -= chunk
            contadorPorDia[dia] += chunk
            alocado = true
            break
          }

          if (alocado) break
        }
      }

      if (!alocado) break
    }
  })

  return distribuicao
}

export async function gerarHorarios(maxTemposPorDia = 4, instituicaoId = null) {
  try {
    let profsQuery = supabase
      .from("professores")
      .select("id, nome, preferencia_periodo, disponibilidade, instituicao_id")
    
    // Se instituicaoId for fornecido, filtrar por ele
    if (instituicaoId) {
      profsQuery = profsQuery.eq("instituicao_id", instituicaoId)
    }
    
    const { data: professores, error: errProf } = await profsQuery
    if (errProf) throw errProf
    if (!professores?.length) {
      console.warn("Nenhum professor cadastrado")
      return []
    }

    let turmasQuery = supabase
      .from("turmas")
      .select("id, nome, sala_id, curso_id, turno, instituicao_id")
    
    if (instituicaoId) {
      turmasQuery = turmasQuery.eq("instituicao_id", instituicaoId)
    }
    
    const { data: turmas, error: errTurmas } = await turmasQuery
    if (errTurmas) throw errTurmas
    if (!turmas?.length) {
      console.warn("Nenhuma turma cadastrada")
      return []
    }

    const { data: pd, error: errPD } = await supabase.from("professor_disciplina").select("*")
    if (errPD) throw errPD

    const { data: td, error: errTD } = await supabase
      .from("turma_disciplina")
      .select("turma_id, disciplina_id, tempos_semanais")
    if (errTD) throw errTD

    const { data: disciplinas, error: errDisc } = await supabase.from("disciplinas").select("*")
    if (errDisc) throw errDisc

    const horariosGerados = []

    const professorOcupacao = {}
    professores.forEach((p) => {
      professorOcupacao[p.id] = {}
      diasDaSemana.forEach((dia) => {
        professorOcupacao[p.id][dia] = []
      })
    })

    const turmaOcupacao = {}
    const turmaDisciplinaProfessor = {}
    turmas.forEach((t) => {
      turmaOcupacao[t.id] = {}
      turmaDisciplinaProfessor[t.id] = {}
      diasDaSemana.forEach((dia) => {
        turmaOcupacao[t.id][dia] = []
      })
      if (t.sala_id) {
        if (!turmaOcupacao[t.sala_id]) {
          turmaOcupacao[t.sala_id] = {}
          diasDaSemana.forEach((dia) => {
            turmaOcupacao[t.sala_id][dia] = []
          })
        }
      }
    })

    const disciplinaProfessores = {}
    ;(pd || []).forEach(({ professor_id, disciplina_id }) => {
      if (!disciplinaProfessores[disciplina_id]) disciplinaProfessores[disciplina_id] = []
      disciplinaProfessores[disciplina_id].push(professor_id)
    })

    const verificarConflitos = (profId, turmaId, salaId, dia, horaInicio) => {
      if (profId && professorOcupacao[profId]?.[dia]?.includes(horaInicio)) {
        return { conflito: true, tipo: "professor" }
      }
      if (turmaOcupacao[turmaId]?.[dia]?.includes(horaInicio)) {
        return { conflito: true, tipo: "turma" }
      }
      if (salaId && turmaOcupacao[salaId]?.[dia]?.includes(horaInicio)) {
        return { conflito: true, tipo: "sala" }
      }
      return { conflito: false }
    }

    const marcarOcupacao = (profId, turmaId, salaId, dia, horaInicio) => {
      if (profId) {
        if (!professorOcupacao[profId]) professorOcupacao[profId] = {}
        if (!professorOcupacao[profId][dia]) professorOcupacao[profId][dia] = []
        professorOcupacao[profId][dia].push(horaInicio)
      }
      if (!turmaOcupacao[turmaId]) turmaOcupacao[turmaId] = {}
      if (!turmaOcupacao[turmaId][dia]) turmaOcupacao[turmaId][dia] = []
      turmaOcupacao[turmaId][dia].push(horaInicio)

      if (salaId) {
        if (!turmaOcupacao[salaId]) turmaOcupacao[salaId] = {}
        if (!turmaOcupacao[salaId][dia]) turmaOcupacao[salaId][dia] = []
        turmaOcupacao[salaId][dia].push(horaInicio)
      }
    }

    const verificarTurno = (professor, turnoTurma) => {
      if (!professor.preferencia_periodo) return true
      const turnoNormalizado = turnosAlias[turnoTurma] || "manha"
      if (Array.isArray(professor.preferencia_periodo)) {
        return professor.preferencia_periodo.some((p) => turnosAlias[p] === turnoNormalizado || p === turnoNormalizado)
      }
      return (
        turnosAlias[professor.preferencia_periodo] === turnoNormalizado ||
        professor.preferencia_periodo === turnoNormalizado
      )
    }

    const turmasEmbaralhadas = shuffleArray(turmas)

    for (const turma of turmasEmbaralhadas) {
      const turmaDisciplinas = (td || []).filter((t) => t.turma_id === turma.id)

      if (!turmaDisciplinas.length) {
        console.log(`Turma ${turma.nome} não tem disciplinas vinculadas`)
        continue
      }

      const disciplinasTurma = turmaDisciplinas
        .map((td) => {
          const disc = disciplinas.find((d) => d.id === td.disciplina_id)
          return {
            ...disc,
            tempos_semanais: td.tempos_semanais || 4,
          }
        })
        .filter(Boolean)

      const turnoNormalizado = turnosAlias[turma.turno] || "manha"
      const horariosTurno = turnos[turnoNormalizado] || turnos.manha

      const distribuicao = distribuirDisciplinasAleatorio(disciplinasTurma, diasDaSemana, maxTemposPorDia)

      distribuicao.forEach((item) => {
        const idx = item.tempoIndex
        if (idx >= horariosTurno.length) return

        const dia = item.dia
        const horario = horariosTurno[idx]
        const disciplina = item.disciplina

        const conflito = verificarConflitos(null, turma.id, turma.sala_id, dia, horario.inicio)
        if (conflito.conflito && conflito.tipo === "turma") {
          return
        }

        let professorSelecionado = null
        const profJaUsado = turmaDisciplinaProfessor[turma.id][disciplina.id]

        if (profJaUsado) {
          const prof = professores.find((p) => p.id === profJaUsado)
          const conflitoProf = verificarConflitos(profJaUsado, turma.id, turma.sala_id, dia, horario.inicio)
          if (prof && !conflitoProf.conflito) {
            professorSelecionado = prof
          }
        } else {
          const professoresVinculados = shuffleArray(disciplinaProfessores[disciplina.id] || [])
          for (const profId of professoresVinculados) {
            const prof = professores.find((p) => p.id === profId)
            const conflitoProf = verificarConflitos(profId, turma.id, turma.sala_id, dia, horario.inicio)
            if (prof && !conflitoProf.conflito && verificarTurno(prof, turnoNormalizado)) {
              professorSelecionado = prof
              turmaDisciplinaProfessor[turma.id][disciplina.id] = prof.id
              break
            }
          }
        }

        if (!professorSelecionado) {
          console.warn(
            `Não foi possível atribuir professor para disciplina ${disciplina.nome || disciplina.id} na turma ${turma.nome || turma.id} em ${dia} ${horario.inicio}`,
          )
          return
        }

        horariosGerados.push({
          professor_id: professorSelecionado.id,
          disciplina_id: disciplina.id,
          turma_id: turma.id,
          dia_semana: dia,
          hora_inicio: horario.inicio,
          hora_fim: horario.fim,
          sala_id: turma.sala_id || null,
          instituicao_id: turma.instituicao_id || instituicaoId,
          created_at: new Date().toISOString(),
        })

        marcarOcupacao(professorSelecionado.id, turma.id, turma.sala_id, dia, horario.inicio)
      })
    }

    if (horariosGerados.length > 0) {
      const { error: deleteError } = await supabase
        .from("horarios")
        .delete()
        .neq("id", "00000000-0000-0000-0000-000000000000")

      if (deleteError) {
        console.error("Erro ao deletar horários:", deleteError)
      }

      const { error: insertError } = await supabase.from("horarios").insert(horariosGerados)
      if (insertError) throw insertError
      console.log(`Horários gerados com sucesso: ${horariosGerados.length}`)
    } else {
      console.warn("Nenhum horário foi gerado. Verifique se há turmas com disciplinas vinculadas.")
    }

    return horariosGerados
  } catch (err) {
    console.error("Erro ao gerar horários:", err.message)
    throw err
  }
}
